/**
 * Coups de pouce — Attendu 1128 « Déterminer a % de c quand a vaut 100, 50,
 * 25, 10, 1 » (automatisme 4e).
 *
 * calcul_* (saisie, QCM, V/F) : entrée PARTAGÉE — un pourcentage est un geste
 * sur le nombre (entier, moitié, quart, ÷ 10, ÷ 100), le quart est la moitié
 * de la moitié, on contrôle (résultat ≤ nombre). L'exemple (revue 2026-09-08) :
 * 360 → 180, 90, 36, 3,6 ; piège 360 ÷ 25 = 14,4. SVG piloté par les flèches de
 * la sheet (data-etapes / data-caption).
 *
 * geste_relier, rappel_tableau_vf : pur rappel, pas d'entrée (doctrine
 * 2026-07-31).
 */

// Bande de 360 partagée : une barre horizontale de 300 px, x de 30 à 330
const XB0 = 30; const LB = 300; const XB = (part) => XB0 + LB * part;
const barre = (y, part, couleur, txt) => `<rect x="${XB0}" y="${y}" width="${(LB * part).toFixed(1)}" height="16" fill="${couleur}" rx="3"/>`
    + `<rect x="${XB(part).toFixed(1)}" y="${y}" width="${(LB * (1 - part)).toFixed(1)}" height="16" fill="#E2E8F0" rx="3"/>`
    + `<text x="${(XB(part) + 6).toFixed(1)}" y="${y + 12}" font-size="10.5" font-weight="700" fill="${couleur}">${txt}</text>`;

const SVG_CALCUL = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 300" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="3"
     aria-label="Calculer des pourcentages de 360, en 3 étapes à faire défiler avec les flèches. Une bande représente 360, c'est 100 pour cent. Étape 1 : 50 pour cent de 360, c'est la moitié, 180. Étape 2 : 25 pour cent de 360, c'est la moitié de la moitié, 90 ; piège : 360 divisé par 25 égale 14,4 est faux. Étape 3 : 10 pour cent de 360, c'est 360 divisé par 10, soit 36 ; 1 pour cent de 360, c'est 360 divisé par 100, soit 3,6">
  <style>
    @keyframes cp1128-pop { from { opacity: 0; } to { opacity: 1; } }
    .cp1128-pop { opacity: 0; animation: cp1128-pop 0.5s ease forwards; }
  </style>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">50 % de 360 : la moitié</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="2" font-family="system-ui,sans-serif">25 % de 360 : la moitié de la moitié</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#B45309" opacity="0" data-caption="3" font-family="system-ui,sans-serif">10 % et 1 % : on décale la virgule</text>

  <!-- ── La bande entière et la moitié (étape 1) ─────────────────────────── -->
  <g data-etape="1" font-family="system-ui,sans-serif">
    <text x="8" y="44" font-size="11" font-weight="700" fill="#1e293b">Le nombre 360, c'est 100 % :</text>
    <rect x="${XB0}" y="50" width="${LB}" height="16" fill="#1E3A8A" rx="3"/>
    <text x="180" y="62" font-size="10.5" font-weight="700" fill="#ffffff" text-anchor="middle">360</text>
    <text x="8" y="90" font-size="11" font-weight="700" fill="#1E3A8A">50 % : la moitié</text>
    ${barre(96, 0.5, '#1E3A8A', '360 ÷ 2 = 180 ✓')}
  </g>

  <!-- ── Le quart (étape 2) ──────────────────────────────────────────────── -->
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1128-pop">
      <text x="8" y="134" font-size="11" font-weight="700" fill="#1D9E75">25 % : le quart, c'est la moitié de la moitié</text>
      ${barre(140, 0.25, '#1D9E75', '180 ÷ 2 = 90 ✓')}
      <text x="8" y="176" font-size="10.5" font-weight="700" fill="#DC2626">360 ÷ 25 = 14,4 ✗  on ne divise pas par 25</text>
    </g>
  </g>

  <!-- ── Le dixième et le centième (étape 3) ─────────────────────────────── -->
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1128-pop">
      <text x="8" y="206" font-size="11" font-weight="700" fill="#B45309">10 % : on divise par 10</text>
      ${barre(212, 0.1, '#B45309', '360 ÷ 10 = 36 ✓')}
      <text x="8" y="248" font-size="11" font-weight="700" fill="#B45309">1 % : on divise par 100</text>
      ${barre(254, 0.01, '#B45309', '360 ÷ 100 = 3,6 ✓')}
      <text x="180" y="292" font-size="12" font-weight="800" fill="#1E3A8A" text-anchor="middle">Chaque pourcentage est un geste : moitié, quart, ÷ 10, ÷ 100.</text>
    </g>
  </g>

  <text x="180" y="292" font-size="11" font-weight="700" fill="#b45309" text-anchor="middle" opacity="0" data-caption="1" font-family="system-ui,sans-serif">↓ Touche les flèches pour avancer, étape par étape ↓</text>
</svg>`.trim();

const AIDE_CALCUL = {
    pages: [
        {
            titre: 'La règle',
            lignes: [
                "**Un pourcentage est un geste sur le nombre** : 100 %, c'est le nombre entier ; 50 %, sa moitié ; 25 %, son quart ; 10 %, le nombre divisé par 10 ; 1 %, le nombre divisé par 100.",
                "**Deux gestes pour le quart** : 25 %, c'est la moitié de la moitié. Et 1 %, c'est le dixième du dixième.",
                "**Vérifie** : le résultat est toujours plus petit que le nombre (ou égal pour 100 %). Diviser par 25 ou enlever 10 ne sont pas des gestes de pourcentage.",
            ],
        },
        { titre: 'Un exemple', svg: SVG_CALCUL },
    ],
};

// ─── Quel pourcentage ? : 9, c'est quel pourcentage de 36 ? ───────────────
const SVG_INVERSE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 270" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="3"
     aria-label="9, c'est quel pourcentage de 36 ? En 3 étapes à faire défiler avec les flèches. Étape 1 : une bande représente 36 ; 9 y tient 4 fois, la bande est partagée en 4 parts de 9. Étape 2 : 4 fois, c'est le quart, donc 25 pour cent. Étape 3 : vérification, 25 pour cent de 36 égale 36 divisé par 4 égale 9 ; piège : 9 pour cent est faux, 9 est la part, pas le pourcentage">
  <style>
    @keyframes cp1128-pop { from { opacity: 0; } to { opacity: 1; } }
    .cp1128-pop { opacity: 0; animation: cp1128-pop 0.5s ease forwards; }
  </style>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">Combien de fois 9 tient-il dans 36 ?</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="2" font-family="system-ui,sans-serif">4 fois : c'est le quart, donc 25 %</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#B45309" opacity="0" data-caption="3" font-family="system-ui,sans-serif">On vérifie en refaisant le geste</text>

  <!-- ── La bande de 36 partagée en 4 (étape 1) ──────────────────────────── -->
  <g data-etape="1" font-family="system-ui,sans-serif">
    <text x="8" y="44" font-size="11" font-weight="700" fill="#1e293b">Question : 9, c'est quel pourcentage de 36 ?</text>
    <text x="8" y="68" font-size="11" font-weight="700" fill="#1E3A8A">Le nombre 36, c'est 100 % :</text>
    ${[0, 1, 2, 3].map((k) => `<rect x="${XB0 + k * 75}" y="74" width="73" height="18" fill="${k === 0 ? '#1E3A8A' : '#DBEAFE'}" stroke="#1E3A8A" stroke-width="1" rx="3"/><text x="${XB0 + k * 75 + 36.5}" y="87" font-size="10.5" font-weight="700" fill="${k === 0 ? '#ffffff' : '#1E3A8A'}" text-anchor="middle">9</text>`).join('')}
    <text x="8" y="114" font-size="11" font-weight="700" fill="#1E3A8A">9 + 9 + 9 + 9 = 36 : la part 9 tient 4 fois dans 36.</text>
  </g>

  <!-- ── Le quart (étape 2) ──────────────────────────────────────────────── -->
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1128-pop">
      <text x="8" y="148" font-size="11.5" font-weight="700" fill="#1D9E75">4 fois, c'est le quart : 9 est le quart de 36.</text>
      <text x="8" y="166" font-size="12.5" font-weight="800" fill="#1D9E75">Le quart, c'est 25 % ✓</text>
      <text x="8" y="184" font-size="10" font-weight="600" fill="#1D9E75">(2 fois : la moitié, 50 % ; 10 fois : 10 % ; 100 fois : 1 % ; 1 fois : 100 %)</text>
    </g>
  </g>

  <!-- ── Vérification et piège (étape 3) ─────────────────────────────────── -->
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1128-pop">
      <text x="8" y="216" font-size="11.5" font-weight="700" fill="#B45309">Vérification : 25 % de 36 = 36 ÷ 4 = 9 ✓</text>
      <text x="8" y="234" font-size="11" font-weight="700" fill="#DC2626">9 % ✗  9 est la part, pas le pourcentage</text>
      <text x="8" y="258" font-size="11.5" font-weight="700" fill="#1e293b">Réponse : 9, c'est 25 % de 36.</text>
    </g>
  </g>

  <text x="180" y="266" font-size="11" font-weight="700" fill="#b45309" text-anchor="middle" opacity="0" data-caption="1" font-family="system-ui,sans-serif">↓ Touche les flèches pour avancer, étape par étape ↓</text>
</svg>`.trim();

const AIDE_INVERSE = {
    pages: [
        {
            titre: 'La règle',
            lignes: [
                "**Compare la part au nombre entier** : combien de fois la part tient-elle dans le nombre ? Si elle y tient 2 fois, c'est la moitié, donc 50 % ; 4 fois, le quart, donc 25 % ; 10 fois, 10 % ; 100 fois, 1 %.",
                "**Si la part est égale au nombre**, c'est 100 %.",
                "**Vérifie en refaisant le geste** : 25 % de 48, c'est 48 ÷ 4 = 12. Si tu retrouves la part, c'est le bon pourcentage.",
            ],
        },
        { titre: 'Un exemple', svg: SVG_INVERSE },
    ],
};

const CP_1128 = {
    calcul_saisie: AIDE_CALCUL,
    calcul_qcm_unique: AIDE_CALCUL,
    calcul_tableau_vf: AIDE_CALCUL,
    geste_qcm_unique: AIDE_INVERSE,
};

export default CP_1128;
